import baseManifest from '../content/artwork.json';
import { getRuntimeArtwork, subscribeContent } from './content-runtime';

export type Placement = 'hero' | 'gallery' | 'original' | 'about' | 'charity' | 'commission' | 'shows';

export interface ArtworkPiece {
  id: string;
  title: string;
  src: string;
  /** Smaller grid preview when available */
  thumb?: string;
  medium?: string;
  year?: number;
  placements: Placement[];
  featured?: boolean;
  forSale?: boolean;
  price?: number;
  description?: string;
  sold?: boolean;
  /** true when added via Studio (localStorage) */
  local?: boolean;
}

/** Prefer thumb for grids; full src for hero / lightbox. */
export function displaySrc(piece: ArtworkPiece, size: 'thumb' | 'full' = 'full'): string {
  if (size === 'thumb' && piece.thumb) return piece.thumb;
  return piece.src;
}

export interface ArtworkManifest {
  version: number;
  pieces: ArtworkPiece[];
}

const STORAGE_KEY = 'su-silent-strokes-artwork-v1';

const BASE: ArtworkManifest = baseManifest as ArtworkManifest;

function readLocal(): ArtworkPiece[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { pieces?: ArtworkPiece[] };
    return Array.isArray(parsed.pieces) ? parsed.pieces : [];
  } catch {
    return [];
  }
}

function writeLocal(pieces: ArtworkPiece[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: BASE.version, pieces }));
  window.dispatchEvent(new CustomEvent('su-artwork-changed'));
}

/** Live KV content + base catalog + Studio overlays (local wins by id). */
export function getManifest(): ArtworkManifest {
  const live = getRuntimeArtwork();
  const local = readLocal();
  const byId = new Map<string, ArtworkPiece>();
  for (const p of BASE.pieces) byId.set(p.id, p);
  for (const p of live.pieces ?? []) byId.set(p.id, { ...byId.get(p.id), ...p } as ArtworkPiece);
  for (const p of local) byId.set(p.id, { ...p, local: true });
  return { version: live.version ?? BASE.version, pieces: [...byId.values()] };
}

export function piecesFor(placement: Placement, opts?: { featuredOnly?: boolean }): ArtworkPiece[] {
  return getManifest()
    .pieces.filter((p) => p.placements.includes(placement))
    .filter((p) => (opts?.featuredOnly ? p.featured : true));
}

export function originalsForSale(): ArtworkPiece[] {
  return getManifest().pieces.filter(
    (p) => p.forSale || p.placements.includes('original'),
  );
}

export function heroPiece(): ArtworkPiece | undefined {
  return heroSlides()[0];
}

/**
 * Home hero carousel: studio multi-portrait first, then up to 4 other hero pieces.
 */
export function heroSlides(maxOthers = 4): ArtworkPiece[] {
  const all = getManifest().pieces;
  const studio = all.find((p) => p.id === 'studio-collection');
  const others = piecesFor('hero')
    .filter((p) => p.id !== 'studio-collection')
    .slice(0, maxOthers);
  if (studio) return [studio, ...others];
  if (others.length) return others;
  return all.filter((p) => p.featured).slice(0, maxOthers + 1);
}

export function addOrUpdatePiece(piece: ArtworkPiece) {
  const local = readLocal().filter((p) => p.id !== piece.id);
  local.unshift({ ...piece, local: true });
  writeLocal(local);
}

export function removeLocalPiece(id: string) {
  writeLocal(readLocal().filter((p) => p.id !== id));
}

export function clearLocalPieces() {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent('su-artwork-changed'));
}

export function exportManifestJson(): string {
  return JSON.stringify(getManifest(), null, 2);
}

export function slugId(title: string): string {
  const base = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
  return `${base || 'piece'}-${Date.now().toString(36)}`;
}

export function formatPrice(price?: number): string {
  if (price == null) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(
    price,
  );
}

export function useArtworkVersion(subscribe: () => void) {
  if (typeof window === 'undefined') return;
  window.addEventListener('su-artwork-changed', subscribe);
  window.addEventListener('storage', subscribe);
  const unsubContent = subscribeContent(subscribe);
  return () => {
    window.removeEventListener('su-artwork-changed', subscribe);
    window.removeEventListener('storage', subscribe);
    unsubContent();
  };
}
